import { useState, useEffect, useCallback } from "react";
import { Linking } from "react-native";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "../../../hooks/useAuth";
import { useAppConfig } from "../../../AppConfigProvider";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useSignupMutation } from "../../../graphql/generated/graphql";
import { z } from "zod";
import SignupForm from "../../../components/auth/SignupForm/SignupForm";
import { calculateAge } from "../../../utils/date";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import Sentry from "../../../utils/sentry";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Platform } from "react-native";
import { announceForAccessibility } from "../../../utils/accessibility";

const signupSchema = z
  .object({
    name: z
      .string()
      .min(1, "First name is required")
      .refine((value) => !value.includes(" "), "For security reasons, please only provide your first name"),
    email: z
      .string()
      .min(1, "Email is required")
      .transform((val) => val.trim().toLowerCase())
      .pipe(z.string().email("Please enter a valid email")),
    password: z
      .string()
      .min(10, "Password must be at least 10 characters long")
      .refine(
        (value) => /[A-Z]/.test(value) && /[\d!@#$%^&*()_+{}":;'<>?,./\\-]/.test(value),
        "Password must contain at least 1 uppercase letter, and 1 number or special character"
      ),
    confirmPassword: z.string().min(1, "Confirm password is required"),
    birthday: z
      .date({
        required_error: "Birthday is required",
      })
      .nullable()
      .refine((value) => value !== null, "Birthday is required")
      .refine((value) => {
        const age = calculateAge(value);
        return age >= 14;
      }, "You must be 14 years old or older to sign up"),
    gender: z.string().min(1, "Please select your gender"),
    hasAgreedToTerms: z.boolean().refine((value) => value === true, "You must agree to the Terms of Service"),
    dailyEmailOptIn: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SignupFormData = z.infer<typeof signupSchema>;

function SignupFormContainer() {
  const posthog = usePostHog();
  const { login } = useAuth();
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { dispatch } = useAppState();
  const [signupMutation] = useSignupMutation();

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [confirmPasswordHidden, setConfirmPasswordHidden] = useState(true);

  const {
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      birthday: null,
      gender: "",
      hasAgreedToTerms: false,
      dailyEmailOptIn: true,
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const handleFieldChange = useCallback(
    (name: keyof SignupFormData, value: any) => {
      setValue(name, value, { shouldValidate: true, shouldTouch: true });
    },
    [setValue]
  );

  const logo = appConfig.logo;
  const termsOfServiceLink = appConfig.termsOfServiceLink;
  const hasEmailVerification = appConfig.hasEmailVerification;

  useEffect(() => {
    posthog?.capture("Signup Page Visited");
  }, [posthog]);

  useEffect(() => {
    if (Platform.OS === "ios" && errorMessage) {
      announceForAccessibility({ message: "Error alert: " + errorMessage });
    }

    Object.keys(errors).forEach((key) => {
      const error = errors[key as keyof typeof errors];
      if (Platform.OS === "ios" && error) {
        announceForAccessibility({ message: error.message || "Field error" });
      }
    });
  }, [errorMessage, errors]);

  useFocusEffect(
    useCallback(() => {
      setErrorMessage("");
      reset();
    }, [reset])
  );

  const handleTermsPress = () => {
    Linking.openURL(termsOfServiceLink);
  };

  const handlePasswordToggle = () => {
    setPasswordHidden((prev) => !prev);
  };

  const handleConfirmPasswordToggle = () => {
    setConfirmPasswordHidden((prev) => !prev);
  };

  const onSubmit = async (data: SignupFormData) => {
    setErrorMessage("");

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      setIsLoading(true);

      const result = await signupMutation({
        variables: {
          email: data.email,
          name: data.name,
          password: data.password,
          gender: data.gender,
          birthday: new Date(data.birthday).toISOString(),
          dailyEmailOptIn: data.dailyEmailOptIn,
          language: "",
          timezone: timezone,
        },
      });

      if (result.data) {
        const age = calculateAge(data.birthday);

        posthog?.identify(result?.data.signup.user.id, {
          name: result?.data.signup.user.name,
          age: age,
          gender: data.gender,
        });
        posthog?.capture("user signup", {
          distinctId: result?.data.signup.user.id,
        });

        const accessToken = result.data.signup.accessToken;
        const refreshToken = result.data.signup.refreshToken;
        await login(accessToken, refreshToken);

        setErrorMessage("");

        dispatch({
          type: actions.SET_META,
          payload: { user: result.data.signup.user, email: data.email },
        });

        if (hasEmailVerification) {
          navigation.navigate("Verify Email");
        } else if (isCompanionMode) {
          navigation.navigate("Companion Home");
        }
      }

      setIsLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(error.message);
      setIsLoading(false);
    }
  };

  const handleSignInPress = () => {
    navigation.navigate("Login");
  };

  const { name, email, password, confirmPassword, birthday, gender, dailyEmailOptIn, hasAgreedToTerms } = watch();

  return (
    <SignupForm
      name={name}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      birthday={birthday}
      gender={gender}
      dailyEmailOptIn={dailyEmailOptIn}
      hasAgreedToTerms={hasAgreedToTerms}
      nameError={errors.name?.message}
      emailError={errors.email?.message}
      passwordError={errors.password?.message}
      confirmPasswordError={errors.confirmPassword?.message}
      birthdayError={errors.birthday?.message}
      genderError={errors.gender?.message}
      termsError={errors.hasAgreedToTerms?.message}
      logo={logo}
      errorMessage={errorMessage}
      isLoading={isLoading}
      onTermsPress={handleTermsPress}
      onSubmit={handleSubmit(onSubmit)}
      onSignInPress={handleSignInPress}
      onNameChange={(val) => handleFieldChange("name", val)}
      onEmailChange={(val) => handleFieldChange("email", val)}
      onPasswordChange={(val) => handleFieldChange("password", val)}
      onConfirmPasswordChange={(val) => handleFieldChange("confirmPassword", val)}
      onBirthdayChange={(val) => handleFieldChange("birthday", val)}
      onGenderChange={(val) => handleFieldChange("gender", val)}
      onNameBlur={() => handleFieldChange("name", name)}
      onEmailBlur={() => handleFieldChange("email", email)}
      onPasswordBlur={() => handleFieldChange("password", password)}
      onConfirmPasswordBlur={() => handleFieldChange("confirmPassword", confirmPassword)}
      onDailyEmailToggle={() => handleFieldChange("dailyEmailOptIn", !dailyEmailOptIn)}
      onTermsToggle={() => handleFieldChange("hasAgreedToTerms", !hasAgreedToTerms)}
      passwordHidden={passwordHidden}
      confirmPasswordHidden={confirmPasswordHidden}
      onPasswordToggle={handlePasswordToggle}
      onConfirmPasswordToggle={handleConfirmPasswordToggle}
      isValid={isValid}
    />
  );
}

export default SignupFormContainer;
